import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'
import type { Project, ScopeGap, ScopeItem, ScsItem, SpecComplianceFlag, Sub } from '../types'
import Badge, { type BadgeColor } from '../components/Badge'
import { navigateTo } from '../lib/nav-target'

const STATUS_COLOR: Record<ScopeItem['status'], BadgeColor> = {
  planned: 'gray',
  in_progress: 'gold',
  complete: 'green',
  deferred: 'gray',
}

function CoverageStat({ label, value, alert }: { label: string; value: number; alert?: boolean }) {
  return (
    <div
      style={{
        minWidth: 110,
        padding: '6px 10px',
        borderRadius: 4,
        background:
          alert && value > 0
            ? 'rgba(204,68,68,0.10)'
            : value > 0
            ? 'rgba(212,175,55,0.10)'
            : 'rgba(255,255,255,0.04)',
      }}
    >
      <div className="muted small" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </div>
      <div style={{ fontSize: '1.05rem', fontWeight: 600 }}>{value}</div>
    </div>
  )
}

const COMMON_CATEGORIES = [
  'site',
  'foundation',
  'framing',
  'roofing',
  'siding',
  'windows',
  'doors',
  'plumbing',
  'electrical',
  'hvac',
  'insulation',
  'drywall',
  'cabinets',
  'flooring',
  'tile',
  'paint',
  'trim',
  'fixtures',
  'landscape',
  'other',
]

export default function ScopeTab({ project }: { project: Project }) {
  const [items, setItems] = useState<ScopeItem[]>([])
  const [subs, setSubs] = useState<Sub[]>([])
  const [scs, setScs] = useState<ScsItem[]>([])
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState<Partial<ScopeItem>>({ status: 'planned' })
  const [filter, setFilter] = useState('')
  const [gaps, setGaps] = useState<ScopeGap[]>([])
  const [flags, setFlags] = useState<SpecComplianceFlag[]>([])

  useEffect(() => {
    api.scope.list(project.id).then(setItems)
    api.subs.list().then(setSubs)
    api.scs.list(project.id).then(setScs)
    api.scopeGaps
      .list(project.id, 'unresolved')
      .then(setGaps)
      .catch(() => setGaps([]))
    api.specCompliance
      .list(project.id, { status: 'open' })
      .then(setFlags)
      .catch(() => setFlags([]))
  }, [project.id])

  async function refreshGaps() {
    setGaps(await api.scopeGaps.list(project.id, 'unresolved'))
  }
  async function refreshFlags() {
    setFlags(await api.specCompliance.list(project.id, { status: 'open' }))
  }
  async function assignGap(g: ScopeGap, trade: string) {
    await api.scopeGaps.update(g.id, { assigned_trade: trade, status: 'assigned' })
    refreshGaps()
  }
  async function confirmGap(g: ScopeGap) {
    await api.scopeGaps.update(g.id, { status: 'confirmed' })
    refreshGaps()
    setItems(await api.scope.list(project.id))
  }
  async function dismissGap(g: ScopeGap) {
    if (!confirm('Dismiss this scope gap?')) return
    await api.scopeGaps.update(g.id, { status: 'dismissed' })
    refreshGaps()
  }
  async function setFlagStatus(f: SpecComplianceFlag, status: SpecComplianceFlag['status']) {
    await api.specCompliance.update(f.id, { status })
    refreshFlags()
  }

  // SOW Coverage analysis (cross-references scope_items / scs_items / sub_projects / sub_agreements)
  const [coverage, setCoverage] = useState<Awaited<ReturnType<typeof api.sowCoverage.run>> | null>(null)
  const [coverageBusy, setCoverageBusy] = useState(false)
  const [coverageError, setCoverageError] = useState<string | null>(null)
  async function runCoverage() {
    setCoverageBusy(true)
    setCoverageError(null)
    try {
      const r = await api.sowCoverage.run(project.id)
      setCoverage(r)
      refreshGaps()
    } catch (e) {
      setCoverageError((e as Error).message)
    } finally {
      setCoverageBusy(false)
    }
  }

  const grouped = useMemo(() => {
    const q = filter.trim().toLowerCase()
    const filtered = q
      ? items.filter(
          (i) =>
            i.scope_item.toLowerCase().includes(q) ||
            i.category.toLowerCase().includes(q) ||
            (i.spec_reference ?? '').toLowerCase().includes(q)
        )
      : items
    const groups = new Map<string, ScopeItem[]>()
    for (const i of filtered) {
      const arr = groups.get(i.category) ?? []
      arr.push(i)
      groups.set(i.category, arr)
    }
    return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [items, filter])

  async function add() {
    if (!draft.category || !draft.scope_item) return
    const item = await api.scope.create(project.id, draft)
    setItems((p) => [...p, item])
    setAdding(false)
    setDraft({ status: 'planned' })
  }

  async function update(s: ScopeItem, patch: Partial<ScopeItem>) {
    const updated = await api.scope.update(s.id, patch)
    setItems((p) => p.map((x) => (x.id === s.id ? updated : x)))
  }

  async function remove(id: string) {
    if (!window.confirm('Delete this scope item? Cannot be undone.')) return
    await api.scope.delete(id)
    setItems((p) => p.filter((x) => x.id !== id))
  }

  return (
    <section>
      <div className="row-between mb-3">
        <div>
          <h2>Master Scope</h2>
          <p className="muted small">
            {items.length} scope items · structured per-category. Maps to subs and SCS lines.
          </p>
        </div>
        <div className="row gap-2">
          <input
            className="search"
            placeholder="Filter…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <button className="btn-primary" onClick={() => setAdding(!adding)}>
            {adding ? 'Cancel' : '+ Item'}
          </button>
        </div>
      </div>

      <div className="callout small mb-3">
        Master Scope is the structured spec/bid/scope per project. Each item names a category,
        what's being done, the Spec Book reference, the responsible sub, and the SCS budget line
        it maps to. Phase 3 AI Site Walk uses this as context.
      </div>

      {/* SOW Coverage panel — cross-reference scope/SCS/subs/agreements */}
      <div className="card mb-3" style={{ background: 'rgba(127,163,195,0.05)' }}>
        <div className="row-between">
          <div>
            <h3 style={{ margin: 0 }}>
              SOW Coverage{' '}
              {coverage && (
                <span
                  style={{
                    background:
                      coverage.coverage_pct >= 95
                        ? '#2ea043'
                        : coverage.coverage_pct >= 75
                        ? '#d4af37'
                        : '#cc4444',
                    color: '#fff',
                    fontSize: '0.75rem',
                    padding: '2px 8px',
                    borderRadius: 3,
                    marginLeft: 6,
                  }}
                >
                  {coverage.coverage_pct}%
                </span>
              )}
            </h3>
            <p className="muted small">
              Cross-reference Master Scope vs SCS lines vs sub roster vs awarded agreements. Identifies orphans, overlaps, uncovered trades, untracked SCS lines.
            </p>
          </div>
          <button className="btn-primary" onClick={runCoverage} disabled={coverageBusy}>
            {coverageBusy ? 'Running…' : '↻ Run coverage analysis'}
          </button>
        </div>
        {coverageError && <div className="card alert mt-2">{coverageError}</div>}
        {coverage && (
          <div className="row mt-2" style={{ gap: '0.6rem', flexWrap: 'wrap' }}>
            <CoverageStat label="Scope items" value={coverage.total_scope_items} />
            <CoverageStat label="SCS lines" value={coverage.total_scs_lines} />
            <CoverageStat label="Subs on project" value={coverage.total_sub_projects} />
            <CoverageStat label="Sub agreements" value={coverage.total_sub_agreements} />
            <CoverageStat label="Orphan scope" value={coverage.orphan_scope_items.length} alert />
            <CoverageStat label="Uncovered trades" value={coverage.uncovered_trades.length} alert />
            <CoverageStat label="Untracked SCS" value={coverage.untracked_scs_lines.length} alert />
            <CoverageStat label="Overlap candidates" value={coverage.overlap_candidates.length} alert />
            <CoverageStat label="Idle subs" value={coverage.idle_sub_projects.length} />
            <CoverageStat label="Gaps written" value={coverage.gaps_inserted} />
          </div>
        )}
      </div>

      {/* Scope gaps from Plan Intelligence (v2 Module 2) */}
      {gaps.length > 0 && (
        <div className="card mb-3" style={{ borderLeft: '3px solid #d4af37' }}>
          <h3 style={{ marginTop: 0 }}>
            Scope gaps to resolve <span className="muted small">({gaps.length})</span>
          </h3>
          <p className="muted small">
            AI flagged these items as ambiguous between two or more trades. Pick a trade per item
            so every piece of work has exactly one owner.
          </p>
          {gaps.map((g) => {
            const candidates: string[] = (() => {
              try {
                return JSON.parse(g.candidate_trades) as string[]
              } catch {
                return []
              }
            })()
            return (
              <div key={g.id} className="card mb-2" style={{ background: 'rgba(255,255,255,0.04)' }}>
                <div className="row-between">
                  <div style={{ flex: 1 }}>
                    <div className="row-title">{g.item_description}</div>
                    {g.ai_reasoning && <div className="muted small mt-1">{g.ai_reasoning}</div>}
                    <div className="row mt-1" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
                      {candidates.map((t) => (
                        <button
                          key={t}
                          className={g.assigned_trade === t ? 'btn-primary' : 'btn-ghost'}
                          style={{ fontSize: '0.78rem', padding: '2px 8px' }}
                          onClick={() => assignGap(g, t)}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="row gap-2">
                    {g.assigned_trade && (
                      <button
                        className="btn-primary small"
                        onClick={() => confirmGap(g)}
                      >
                        Confirm → scope
                      </button>
                    )}
                    <button className="btn-ghost small" onClick={() => dismissGap(g)}>
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Spec Book compliance flags from Plan Intelligence (v2 Module 2) */}
      {flags.length > 0 && (
        <div className="card mb-3" style={{ borderLeft: '3px solid #cc4444' }}>
          <h3 style={{ marginTop: 0 }}>
            Spec Book compliance flags <span className="muted small">({flags.length} open)</span>
          </h3>
          <p className="muted small">
            Heritage Spec Book is the floor. Anything below standard must be addressed before
            construction starts.
          </p>
          {flags.map((f) => (
            <div
              key={f.id}
              className="card mb-2"
              style={{
                background:
                  f.severity === 'violation'
                    ? 'rgba(204,68,68,0.10)'
                    : f.severity === 'warning'
                    ? 'rgba(212,175,55,0.10)'
                    : 'rgba(255,255,255,0.04)',
              }}
            >
              <div className="row-between">
                <div style={{ flex: 1 }}>
                  <div className="row-title">
                    <span
                      style={{
                        background:
                          f.severity === 'violation'
                            ? '#cc4444'
                            : f.severity === 'warning'
                            ? '#d4af37'
                            : '#666',
                        color: '#fff',
                        fontSize: '0.7rem',
                        padding: '1px 6px',
                        borderRadius: 3,
                        marginRight: 6,
                        textTransform: 'uppercase',
                      }}
                    >
                      {f.severity}
                    </span>
                    {f.category ?? f.spec_section_title}
                  </div>
                  <div className="mt-1 small">
                    <strong>Plan:</strong> {f.plan_value ?? '—'}
                    <br />
                    <strong>Spec:</strong> {f.spec_value ?? '—'}
                  </div>
                  {f.ai_explanation && <div className="muted small mt-1">{f.ai_explanation}</div>}
                  <div className="muted small mt-1 row gap-2" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
                    {f.spec_subsection_id && f.spec_subsection_title ? (
                      <button
                        className="link"
                        style={{ fontSize: '0.75rem' }}
                        onClick={() =>
                          navigateTo({
                            tab: 'spec',
                            specSubsectionId: f.spec_subsection_id!,
                          })
                        }
                        title="Open Spec Book at this subsection"
                      >
                        📘 {f.spec_section_title} → {f.spec_subsection_title}
                      </button>
                    ) : (
                      <span className="muted small">Spec Book: (no spec link)</span>
                    )}
                    {f.plan_sheet_id && f.plan_version_id ? (
                      <button
                        className="link"
                        style={{ fontSize: '0.75rem' }}
                        onClick={() =>
                          navigateTo({
                            tab: 'plans',
                            planVersionId: f.plan_version_id!,
                            planSheetId: f.plan_sheet_id!,
                            planSheetPage: f.plan_sheet_page ?? undefined,
                          })
                        }
                        title="Open Plans tab at this sheet"
                      >
                        📐 Sheet {f.plan_sheet_number ?? '—'}, p{f.plan_sheet_page ?? '—'}
                      </button>
                    ) : (
                      <span className="muted small">Plan: (no sheet link)</span>
                    )}
                    {f.related_plan_sheets && f.related_plan_sheets.length > 0 && (
                      <details>
                        <summary
                          className="link"
                          style={{ cursor: 'pointer', fontSize: '0.75rem' }}
                        >
                          +{f.related_plan_sheets.length} more
                        </summary>
                        <div className="row gap-2 mt-1" style={{ flexWrap: 'wrap' }}>
                          {f.related_plan_sheets.map((rs) => (
                            <button
                              key={rs.id}
                              className="link"
                        style={{ fontSize: '0.75rem' }}
                              onClick={() =>
                                navigateTo({
                                  tab: 'plans',
                                  planVersionId: f.plan_version_id!,
                                  planSheetId: rs.id,
                                  planSheetPage: rs.page_in_pdf,
                                })
                              }
                            >
                              📐 Sheet {rs.sheet_number ?? '—'}, p{rs.page_in_pdf}
                            </button>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                </div>
                <div className="row gap-2">
                  <button className="btn-ghost small" onClick={() => setFlagStatus(f, 'resolved')}>
                    Resolved
                  </button>
                  <button
                    className="btn-ghost small"
                    onClick={() => setFlagStatus(f, 'accepted_as_is')}
                  >
                    Accept as-is
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {adding && (
        <div className="card mb-3">
          <label className="form-row">
            <span>Category</span>
            <select
              value={draft.category ?? ''}
              onChange={(e) => setDraft({ ...draft, category: e.target.value })}
            >
              <option value="">— select —</option>
              {COMMON_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label className="form-row">
            <span>Scope item</span>
            <input
              value={draft.scope_item ?? ''}
              onChange={(e) => setDraft({ ...draft, scope_item: e.target.value })}
              placeholder='Install 2x6 16"OC exterior walls'
            />
          </label>
          <label className="form-row">
            <span>Spec Book ref</span>
            <input
              value={draft.spec_reference ?? ''}
              onChange={(e) => setDraft({ ...draft, spec_reference: e.target.value })}
              placeholder="§4.2 Framing"
            />
          </label>
          <label className="form-row">
            <span>Responsible sub</span>
            <select
              value={draft.responsible_sub_id ?? ''}
              onChange={(e) =>
                setDraft({ ...draft, responsible_sub_id: e.target.value || null })
              }
            >
              <option value="">— none —</option>
              {subs.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.company_name}
                </option>
              ))}
            </select>
          </label>
          <label className="form-row">
            <span>SCS line</span>
            <select
              value={draft.scs_item_id ?? ''}
              onChange={(e) => setDraft({ ...draft, scs_item_id: e.target.value || null })}
            >
              <option value="">— none —</option>
              {scs.map((s) => (
                <option key={s.id} value={s.id}>
                  L{s.line_number} · {s.item}
                </option>
              ))}
            </select>
          </label>
          <button className="btn-primary" onClick={add}>
            Add scope item
          </button>
        </div>
      )}

      {items.length === 0 && !adding && (
        <p className="muted">
          No scope items yet. Start with framing, foundation, mechanical, and the trades you bid
          for Oakridge.
        </p>
      )}

      {grouped.map(([category, group]) => (
        <div key={category} className="card mb-2">
          <h3 style={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 13 }}>
            {category} <span className="muted small">({group.length})</span>
          </h3>
          {group.map((s) => (
            <div key={s.id} className="row-list">
              <div className="grow">
                <div className="row-title">{s.scope_item}</div>
                <div className="muted small">
                  {s.spec_reference ?? '—'}
                  {s.responsible_sub_name && ` · ${s.responsible_sub_name}`}
                  {s.scs_item_name && ` · SCS: ${s.scs_item_name}`}
                </div>
                {s.notes && <div className="muted small mt-1">{s.notes}</div>}
              </div>
              <div className="text-right row gap-2">
                <select
                  className="cell-input"
                  style={{ minWidth: 110 }}
                  value={s.status}
                  onChange={(e) => update(s, { status: e.target.value as ScopeItem['status'] })}
                >
                  <option value="planned">planned</option>
                  <option value="in_progress">in progress</option>
                  <option value="complete">complete</option>
                  <option value="deferred">deferred</option>
                </select>
                <Badge color={STATUS_COLOR[s.status]}>{s.status.replace('_', ' ')}</Badge>
                <button className="btn-ghost small" onClick={() => remove(s.id)}>
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      ))}
    </section>
  )
}
